// LoginPage.jsx
import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff, Mail, Lock, ArrowRight, LogIn } from 'lucide-react';
import { Banner } from '@shopify/polaris'; // Import Banner component
import './AuthPages.css';

const API = process.env.REACT_APP_API_URL || "";

export default function LoginPage() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract all relevant URL query parameters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params;
  }, [location.search]);

  // Derive these values directly from the memoized queryParams
  const returnToPath = queryParams.get("return_to"); // The original page they wanted to go to (e.g., ConnectSuccessPage URL, which could contain Shopify params)
  const shopDomain = queryParams.get("shop"); // These might still be present if passed directly
  const siteId = queryParams.get("site_id");
  const siteToken = queryParams.get("site_token");
  const accessTokenFromShopify = queryParams.get("access_token");
  const code = queryParams.get("code");
  const hmac = queryParams.get("hmac");
  const state = queryParams.get("state");
  const platform = queryParams.get("platform"); // Added platform
  const storeHash = queryParams.get("store_hash"); // Added store_hash

  // Read message from location.state for session expiry notifications
  const sessionExpiredMessage = location.state?.message;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear sessionExpiredMessage after it's displayed once
  useEffect(() => {
    if (sessionExpiredMessage) {
      // Use replace state to clear the message so it doesn't reappear on back navigation
      // Ensure we preserve other state properties if any
      const newState = { ...location.state };
      delete newState.message;
      navigate(location.pathname + location.search, { replace: true, state: newState });
    }
  }, [sessionExpiredMessage, location.pathname, location.search, location.state, navigate]);


  // Helper function to handle redirection after successful authentication
  const handleAuthSuccess = async (jwt_access_token) => {
    console.log('[LoginPage] handleAuthSuccess called after successful JWT acquisition.');

    // Step 1: Log the user into your frontend AuthContext
    login(jwt_access_token);

    // Determine the final redirect path based on return_to and BigCommerce flow detection
    const isBigCommerceFlow = platform === 'BigCommerce' && siteId && siteToken && storeHash; // Updated BigCommerce flow check
    let finalRedirectPath;
    if (isBigCommerceFlow) {
      // go straight to Connect page
      finalRedirectPath = `/connect-bigcommerce` +
        `?site_id=${encodeURIComponent(siteId)}` +
        `&site_token=${encodeURIComponent(siteToken)}` +
        `&store_hash=${encodeURIComponent(storeHash)}`;
    } else if (returnToPath) {
      finalRedirectPath = returnToPath;
    } else {
      finalRedirectPath = '/home';
    }
    console.log(`[LoginPage] Final Redirecting to: ${finalRedirectPath}`);
    navigate(finalRedirectPath, { replace: true });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        body: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      // inside handleSubmit, right after the fetch() call:
      if (!res.ok) {
        let errorMessage = 'Login failed. Please try again.';
        const errData = await res.json().catch(() => null);

        if (res.status === 423) {
          errorMessage = errData?.detail || 'Account is temporarily locked. Please try again later.';
        } else if (res.status === 429) {
          errorMessage = 'Too many attempts. Please wait before trying again.';
        } else if (res.status === 401) {
          errorMessage = errData?.detail || 'Invalid email or password. Please check your credentials.';
        } else if (res.status === 404) {
          errorMessage = errData?.detail || 'Account not found. Please sign up or check your email.';
        } else if (errData && errData.detail) {
          errorMessage = errData.detail;
        } else {
          errorMessage = `Server error: Status ${res.status}`;
        }

        throw new Error(errorMessage);
      }

      const { access_token } = await res.json();
      console.log('[LoginPage] Login API call successful. Calling handleAuthSuccess.');
      handleAuthSuccess(access_token); // Call the unified success handler with JWT

    } catch (err) {
      console.error('[LoginPage] Login API error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onGoogleSuccess = ({ credential }) => {
    setError(null);
    setLoading(true);
    console.log('[LoginPage] Google login initiated.');
    fetch(`${API}/auth/oauth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: credential }),
    })
      .then(r => {
        if (!r.ok) {
          return r.json().then(errData => {
            throw new Error(errData?.detail || `Google login failed: Status ${r.status}`);
          }).catch(() => {
            throw new Error(`Google login failed: Status ${r.status}`);
          });
        }
        return r.json();
      })
      .then(data => {
        console.log('[LoginPage] Google login API call successful. Calling handleAuthSuccess.');
        handleAuthSuccess(data.access_token); // Call the unified success handler with JWT
      })
      .catch(err => {
        console.error('[LoginPage] Google Login failed', err);
        setError(err.message || 'Google login failed.');
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="auth-page">
      <div className="auth-container-modern">
        {/* Header Section */}
        <div className="auth-header">
          <div className="auth-brand">
            <div className="brand-icon">
              <img src={`${process.env.PUBLIC_URL}/AdobeStock_133441382.png`} alt="Logo" className="brand-icon" />
            </div>
            <Link to="/" className="auth-link"><h1>BaoBrain</h1></Link>
          </div>
          <div className="auth-welcome">
            <h2>Welcome back</h2>
            <p>Sign in to your account to continue</p>
          </div>
        </div>

        {/* Display session expired message */}
        {sessionExpiredMessage && (
          <Banner status="warning" title="Session Expired">
            {sessionExpiredMessage}
          </Banner>
        )}

        {/* Form Section */}
        <div className="auth-form-container">
          <form className="auth-form-modern" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={20} />
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* INSERT THIS BLOCK */}
            <div className="form-group">
              <div style={{ textAlign: 'right', marginTop: '-10px', marginBottom: '10px' }}>
                <Link to="/forgot-password" className="auth-link" style={{ fontSize: '14px' }}>
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <div className="auth-error-modern">
                <div className="error-content">
                  <span className="error-text">{error}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Sign in
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Google Sign-In */}
          <div className="auth-divider">
            <span>Or continue with</span>
          </div>

          <div className="google-auth-wrapper">
            <GoogleLogin
              onSuccess={onGoogleSuccess}
              onError={() => {
                setError('Google login failed');
                console.error('[LoginPage] Google login onError triggered.');
              }}
              useOneTap={false}
              auto_select={false}
            />
          </div>

          {/* Footer Links */}
          <div className="auth-footer">
            <p>
              Don't have an account?{' '}
              <Link
                to={
                  `/signup` +
                  `?shop=${encodeURIComponent(shopDomain || '')}` +
                  `&site_id=${encodeURIComponent(siteId || '')}` +
                  `&site_token=${encodeURIComponent(siteToken || '')}` +
                  `&access_token=${encodeURIComponent(accessTokenFromShopify || '')}` +
                  `&code=${encodeURIComponent(code || '')}` +
                  `&hmac=${encodeURIComponent(hmac || '')}` +
                  `&state=${encodeURIComponent(state || '')}` +
                  `&platform=${encodeURIComponent(platform || '')}` + // Added platform
                  `&store_hash=${encodeURIComponent(storeHash || '')}` + // Added store_hash
                  `&return_to=${encodeURIComponent(returnToPath || location.pathname + location.search)}`
                }
                className="auth-link"
              >
                Create one
                <ArrowRight size={14} />
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Background Design */}
      <div className="auth-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>
    </div>
  );
}